"use strict";
/**
 * Registry centralisée de tous les schémas Zod pour les prompts IA
 * Cette registry mappe chaque type/slug de config IA au fragment de schéma Zod
 * utilisé pour valider et parser la réponse de l'IA.
 *
 * C'est la source unique de vérité : backend validation + frontend form generation
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.PROMPT_SCHEMA_REGISTRY = exports.AI_CONFIG_TYPES_WITHOUT_PROMPT_SCHEMA = void 0;
exports.getPromptSchema = getPromptSchema;
exports.getAvailablePromptSchemaTypes = getAvailablePromptSchemaTypes;
const ai_actions_1 = require("./schemas/ai-actions");
const candidate_opportunity_scoring_1 = require("./schemas/candidate-opportunity-scoring");
const dt_1 = require("./schemas/dt");
const interview_preparation_1 = require("./schemas/interview-preparation");
const opportunity_1 = require("./schemas/opportunity");
const profile_matching_1 = require("./schemas/profile-matching");
const profile_matching_scoring_rubric_1 = require("./schemas/profile-matching-scoring-rubric");
/**
 * Types canoniques qui ne disposent d'aucun formulaire de prompt
 * dans les applications Web ou BO.
 */
exports.AI_CONFIG_TYPES_WITHOUT_PROMPT_SCHEMA = [
    'ai-action-speech-transcribe',
    'ai-speech-transcribe-realtime',
    'creation-boolean-request',
    'enrich-dt-neylia-v2',
    'enrich-dt-basic-mini',
    'enrich-dt-field-experience-basic-mini',
    'enrich-dt-field-training-basic-mini',
    'enrich-dt-field-languages-basic-mini',
    'enrich-dt-field-expertiseAreas-basic-mini',
    'enrich-dt-field-tools-basic-mini',
    'enrich-dt-field-references-basic-mini',
    'enrich-dt-field-references-advanced-mini',
    'enrich-company',
    'enrich-company-field-country',
    'enrich-company-field-expertiseArea',
    'enrich-company-field-parentCompany',
];
/**
 * Registry maître : type/slug → schéma Zod
 */
exports.PROMPT_SCHEMA_REGISTRY = {
    // ─── DT Enrichissement - Générale ───────────────────────────────
    'enrich-dt-basic': {
        zodSchema: dt_1.dtSchema.entity,
        description: 'Enrichissement CV complet',
        textareas: [
            { key: 'prepromptCandidateDatas', label: 'Pre-prompt des donnees du candidat' },
            { key: 'init', label: 'Prompt initial' },
        ],
    },
    'enrich-dt': {
        zodSchema: dt_1.dtSchema.entity,
        description: 'Enrichissement DT complet',
        textareas: [
            { key: 'prepromptCandidateDatas', label: 'Pre-prompt des donnees du candidat' },
            { key: 'prepromptCandidateOpportunity', label: 'Pre-prompt de la fiche de poste' },
            { key: 'init', label: 'Prompt initial' },
        ],
    },
    'enrich-dt-mini': {
        zodSchema: dt_1.dtSchema.entity,
        description: 'Enrichissement DT complet (mini)',
        textareas: [
            { key: 'prepromptCandidateDatas', label: 'Pre-prompt des donnees du candidat' },
            { key: 'prepromptCandidateOpportunity', label: 'Pre-prompt de la fiche de poste' },
            { key: 'init', label: 'Prompt initial' },
        ],
    },
    'import-dt': {
        zodSchema: dt_1.dtSchema.entity,
        description: 'Import de donnees DT',
        textareas: [
            { key: 'prepromptCandidateDatas', label: 'Pre-prompt des donnees du candidat' },
            { key: 'prepromptCandidateOpportunity', label: 'Pre-prompt de la fiche de poste' },
            { key: 'init', label: 'Prompt initial' },
        ],
    },
    // ─── DT Enrichissement - Champs individuels ──────────────────
    'enrich-dt-field-experience': {
        zodSchema: dt_1.dtSchema.asideFields.experience,
        description: 'Enrichissement du champ Experience sélection',
        textareas: [{ key: 'init', label: 'Prompt initial' }],
    },
    'enrich-dt-field-experience-basic': {
        zodSchema: dt_1.dtSchema.asideFields.experience,
        description: 'Enrichissement du champ Experience sélection (basic / extraction stricte)',
        textareas: [{ key: 'init', label: 'Prompt initial' }],
    },
    'enrich-dt-field-experience-advanced': {
        zodSchema: dt_1.dtSchema.asideFields.experience,
        description: 'Enrichissement du champ Experience sélection (advanced / enrichissement contrôlé)',
        textareas: [{ key: 'init', label: 'Prompt initial' }],
    },
    'enrich-dt-field-experience-mini': {
        zodSchema: dt_1.dtSchema.asideFields.experience,
        description: 'Enrichissement du champ Experience sélection (mini)',
        textareas: [{ key: 'init', label: 'Prompt initial' }],
    },
    // ─── DT Enrichissement - Champs de Références ──────────────────
    'enrich-dt-field-references-content-basic': {
        zodSchema: dt_1.dtSchema.asideFields.referencesContent,
        description: 'Enrichissement des références (contenu basique / extraction stricte)',
        textareas: [{ key: 'init', label: 'Prompt initial' }],
    },
    'enrich-dt-field-references-content-advanced': {
        zodSchema: dt_1.dtSchema.asideFields.referencesContent,
        description: 'Enrichissement des références (contenu avancé / enrichissement contrôlé)',
        textareas: [{ key: 'init', label: 'Prompt initial' }],
    },
    'enrich-dt-field-references-identity-basic': {
        zodSchema: dt_1.dtSchema.asideFields.referencesIdentity,
        description: 'Enrichissement des références (identité basique / extraction stricte)',
        textareas: [{ key: 'init', label: 'Prompt initial' }],
    },
    'enrich-dt-field-references-identity-advanced': {
        zodSchema: dt_1.dtSchema.asideFields.referencesIdentity,
        description: 'Enrichissement des références (identité avancée / enrichissement contrôlé)',
        textareas: [{ key: 'init', label: 'Prompt initial' }],
    },
    // ─── DT - Références Académiques & Personnelles ─────────────
    'enrich-dt-field-references-identity-academic-personal-basic': {
        zodSchema: dt_1.dtSchema.asideFields.referencesIdentity,
        description: "Extraction d'identité références (Acad/Perso) - Basic",
        textareas: [{ key: 'init', label: 'Prompt initial' }],
    },
    'enrich-dt-field-references-identity-academic-personal-advanced': {
        zodSchema: dt_1.dtSchema.asideFields.referencesIdentity,
        description: "Extraction d'identité références (Acad/Perso) - Advanced",
        textareas: [{ key: 'init', label: 'Prompt initial' }],
    },
    'enrich-dt-field-references-content-academic-personal-basic': {
        zodSchema: dt_1.dtSchema.asideFields.referencesContent,
        description: 'Extraction de contenu références (Acad/Perso) - Basic',
        textareas: [{ key: 'init', label: 'Prompt initial' }],
    },
    'enrich-dt-field-references-content-academic-personal-advanced': {
        zodSchema: dt_1.dtSchema.asideFields.referencesContent,
        description: 'Extraction de contenu références (Acad/Perso) - Advanced',
        textareas: [{ key: 'init', label: 'Prompt initial' }],
    },
    // ─── DT Enrichissement - Splitter de Références ──────────────────
    'enrich-dt-field-references-splitter-basic': {
        zodSchema: dt_1.dtSchema.asideFields.referencesSplitter,
        description: 'Enrichissement des références (Splitter / Strict Extraction)',
        textareas: [{ key: 'init', label: 'Prompt initial' }],
    },
    'enrich-dt-field-references-splitter-advanced': {
        zodSchema: dt_1.dtSchema.asideFields.referencesSplitter,
        description: 'Enrichissement des références (Splitter / Découpage)',
        textareas: [{ key: 'init', label: 'Prompt initial' }],
    },
    // ─── DT Enrichissement - Dispatcher de Références ──────────────────
    'enrich-dt-field-references-dispatcher-basic': {
        zodSchema: dt_1.dtSchema.asideFields.referencesDispatcher,
        description: 'Dispatcher des références (basic / extraction stricte)',
        textareas: [{ key: 'init', label: 'Prompt initial' }],
    },
    'enrich-dt-field-references-dispatcher-advanced': {
        zodSchema: dt_1.dtSchema.asideFields.referencesDispatcher,
        description: 'Dispatcher des références (advanced / enrichissement contrôlé)',
        textareas: [{ key: 'init', label: 'Prompt initial' }],
    },
    'enrich-dt-field-expertiseAreas': {
        zodSchema: dt_1.dtSchema.asideFields.expertiseAreas,
        description: 'Enrichissement des secteurs expertise',
        textareas: [{ key: 'init', label: 'Prompt initial' }],
    },
    'enrich-dt-field-expertiseAreas-basic': {
        zodSchema: dt_1.dtSchema.asideFields.expertiseAreas,
        description: 'Enrichissement des secteurs expertise (basic / extraction stricte)',
        textareas: [{ key: 'init', label: 'Prompt initial' }],
    },
    'enrich-dt-field-expertiseAreas-advanced': {
        zodSchema: dt_1.dtSchema.asideFields.expertiseAreas,
        description: 'Enrichissement des secteurs expertise (advanced / enrichissement contrôlé)',
        textareas: [{ key: 'init', label: 'Prompt initial' }],
    },
    'enrich-dt-field-expertiseAreas-mini': {
        zodSchema: dt_1.dtSchema.asideFields.expertiseAreas,
        description: 'Enrichissement des secteurs expertise (mini)',
        textareas: [{ key: 'init', label: 'Prompt initial' }],
    },
    'enrich-dt-field-languages': {
        zodSchema: dt_1.dtSchema.asideFields.languages,
        description: 'Enrichissement des langues',
        textareas: [{ key: 'init', label: 'Prompt initial' }],
    },
    'enrich-dt-field-languages-basic': {
        zodSchema: dt_1.dtSchema.asideFields.languages,
        description: 'Enrichissement des langues (basic / extraction stricte)',
        textareas: [{ key: 'init', label: 'Prompt initial' }],
    },
    'enrich-dt-field-languages-advanced': {
        zodSchema: dt_1.dtSchema.asideFields.languages,
        description: 'Enrichissement des langues (advanced / enrichissement contrôlé)',
        textareas: [{ key: 'init', label: 'Prompt initial' }],
    },
    'enrich-dt-field-languages-mini': {
        zodSchema: dt_1.dtSchema.asideFields.languages,
        description: 'Enrichissement des langues (mini)',
        textareas: [{ key: 'init', label: 'Prompt initial' }],
    },
    'enrich-dt-field-references': {
        zodSchema: dt_1.dtSchema.asideFields.references,
        description: 'Enrichissement des references',
        textareas: [{ key: 'init', label: 'Prompt initial' }],
    },
    'enrich-dt-field-references-basic': {
        zodSchema: dt_1.dtSchema.asideFields.references,
        description: 'Enrichissement des references (basic / extraction stricte)',
        textareas: [{ key: 'init', label: 'Prompt initial' }],
    },
    'enrich-dt-field-references-advanced': {
        zodSchema: dt_1.dtSchema.asideFields.references,
        description: 'Enrichissement des references (advanced / enrichissement contrôlé)',
        textareas: [{ key: 'init', label: 'Prompt initial' }],
    },
    'enrich-dt-field-references-mini': {
        zodSchema: dt_1.dtSchema.asideFields.references,
        description: 'Enrichissement des references (mini)',
        textareas: [{ key: 'init', label: 'Prompt initial' }],
    },
    'enrich-dt-field-skills': {
        zodSchema: dt_1.dtSchema.asideFields.skills,
        description: 'Enrichissement des competences',
        textareas: [{ key: 'init', label: 'Prompt initial' }],
    },
    'enrich-dt-field-skills-basic': {
        zodSchema: dt_1.dtSchema.asideFields.skills,
        description: 'Enrichissement des competences (basic / extraction stricte)',
        textareas: [{ key: 'init', label: 'Prompt initial' }],
    },
    'enrich-dt-field-skills-advanced': {
        zodSchema: dt_1.dtSchema.asideFields.skills,
        description: 'Enrichissement des competences (advanced / enrichissement contrôlé)',
        textareas: [{ key: 'init', label: 'Prompt initial' }],
    },
    'enrich-dt-field-skills-mini': {
        zodSchema: dt_1.dtSchema.asideFields.skills,
        description: 'Enrichissement des competences (mini)',
        textareas: [{ key: 'init', label: 'Prompt initial' }],
    },
    'enrich-dt-field-tools': {
        zodSchema: dt_1.dtSchema.asideFields.tools,
        description: 'Enrichissement des outils',
        textareas: [{ key: 'init', label: 'Prompt initial' }],
    },
    'enrich-dt-field-tools-basic': {
        zodSchema: dt_1.dtSchema.asideFields.tools,
        description: 'Enrichissement des outils (basic / extraction stricte)',
        textareas: [{ key: 'init', label: 'Prompt initial' }],
    },
    'enrich-dt-field-tools-advanced': {
        zodSchema: dt_1.dtSchema.asideFields.tools,
        description: 'Enrichissement des outils (advanced / enrichissement contrôlé)',
        textareas: [{ key: 'init', label: 'Prompt initial' }],
    },
    'enrich-dt-field-tools-mini': {
        zodSchema: dt_1.dtSchema.asideFields.tools,
        description: 'Enrichissement des outils (mini)',
        textareas: [{ key: 'init', label: 'Prompt initial' }],
    },
    'enrich-dt-field-training': {
        zodSchema: dt_1.dtSchema.asideFields.training,
        description: 'Enrichissement de la formation',
        textareas: [{ key: 'init', label: 'Prompt initial' }],
    },
    'enrich-dt-field-training-basic': {
        zodSchema: dt_1.dtSchema.asideFields.training,
        description: 'Enrichissement de la formation (basic / extraction stricte)',
        textareas: [{ key: 'init', label: 'Prompt initial' }],
    },
    'enrich-dt-field-training-advanced': {
        zodSchema: dt_1.dtSchema.asideFields.training,
        description: 'Enrichissement de la formation (advanced / enrichissement contrôlé)',
        textareas: [{ key: 'init', label: 'Prompt initial' }],
    },
    'enrich-dt-field-training-mini': {
        zodSchema: dt_1.dtSchema.asideFields.training,
        description: 'Enrichissement de la formation (mini)',
        textareas: [{ key: 'init', label: 'Prompt initial' }],
    },
    'enrich-dt-field-activityAreas': {
        zodSchema: dt_1.dtSchema.asideFields.activityAreas,
        description: 'Enrichissement des domaines activite',
        textareas: [{ key: 'init', label: 'Prompt initial' }],
    },
    'enrich-dt-field-activityAreas-basic': {
        zodSchema: dt_1.dtSchema.asideFields.activityAreas,
        description: 'Enrichissement des domaines activite (basic / extraction stricte)',
        textareas: [{ key: 'init', label: 'Prompt initial' }],
    },
    'enrich-dt-field-activityAreas-advanced': {
        zodSchema: dt_1.dtSchema.asideFields.activityAreas,
        description: 'Enrichissement des domaines activite (advanced / enrichissement contrôlé)',
        textareas: [{ key: 'init', label: 'Prompt initial' }],
    },
    'enrich-dt-field-activityAreas-mini': {
        zodSchema: dt_1.dtSchema.asideFields.activityAreas,
        description: 'Enrichissement des domaines activite (mini)',
        textareas: [{ key: 'init', label: 'Prompt initial' }],
    },
    'enrich-dt-field-references-identity': {
        zodSchema: dt_1.dtSchema.asideFields.referencesIdentity,
        description: 'Enrichissement des références (Identité - Zéro Censure)',
        textareas: [
            { key: 'init', label: 'Prompt initial' },
            { key: 'outputJson.references.value', label: 'Instruction globale' },
            { key: 'outputJson.references.children.company.value', label: 'Instruction Entreprise (ESN/Client)' },
        ],
    },
    'enrich-dt-field-references-content': {
        zodSchema: dt_1.dtSchema.asideFields.referencesContent,
        description: 'Enrichissement des références (Contenu - 100% Anonyme)',
        textareas: [
            { key: 'init', label: 'Prompt initial' },
            { key: 'outputJson.references.value', label: 'Instruction globale' },
            { key: 'outputJson.references.children.description.children.context.value', label: 'Instruction Contexte Global' },
            { key: 'outputJson.references.children.projects.value', label: 'Instruction Projets (Anti-invention)' },
            {
                key: 'outputJson.references.children.projects.children.description.children.context.value',
                label: 'Instruction Contexte Projet',
            },
        ],
    },
    'enrich-dt-field-references-splitter': {
        zodSchema: dt_1.dtSchema.asideFields.referencesSplitter,
        description: 'Enrichissement des références (Splitter / Découpage)',
        textareas: [
            { key: 'init', label: 'Prompt initial' },
            { key: 'outputJson.experiences.value', label: 'Instruction globale' },
        ],
    },
    // ─── DT Entité ──────────────────────────────────────────────
    'enrich-entity-sheet': {
        zodSchema: dt_1.dtSchema.entitySheet,
        description: 'Enrichissement fiche entite',
        textareas: [
            { key: 'init', label: 'Prompt initial' },
            {
                key: 'outputJson.administrative.children.salaryEvidence.value',
                label: 'Extraction des preuves de rémunération',
            },
        ],
    },
    'enrich-entity-sheet-field-informations-civility': {
        zodSchema: dt_1.dtSchema.entitySheetAsideFields['informations-civility'],
        description: 'Enrichissement de la civilite',
        textareas: [{ key: 'init', label: 'Prompt initial' }],
    },
    // ─── Opportunité Enrichissement ──────────────────────────────
    'enrich-opportunity': {
        zodSchema: opportunity_1.opportunitySchema.general,
        description: 'Enrichissement fiche poste complet',
        textareas: [
            { key: 'prepromptCandidateDatas', label: 'Pre-prompt des donnees du candidat' },
            { key: 'prepromptCandidateOpportunity', label: 'Pre-prompt de la fiche de poste' },
            { key: 'init', label: 'Prompt initial' },
        ],
    },
    'import-opportunity': {
        zodSchema: opportunity_1.opportunitySchema.general,
        description: 'Import fiche poste',
        textareas: [
            { key: 'prepromptCandidateDatas', label: 'Pre-prompt des donnees du candidat' },
            { key: 'prepromptCandidateOpportunity', label: 'Pre-prompt de la fiche de poste' },
            { key: 'init', label: 'Prompt initial' },
        ],
    },
    // ─── Opportunité - Champs individuels ────────────────────────
    'enrich-opportunity-field-activityAreas': {
        zodSchema: opportunity_1.opportunitySchema.asideFields.activityAreas,
        description: 'Enrichissement des domaines activite',
        textareas: [{ key: 'init', label: 'Prompt initial' }],
    },
    'enrich-opportunity-field-expertiseArea': {
        zodSchema: opportunity_1.opportunitySchema.asideFields.expertiseArea,
        description: 'Enrichissement du secteur expertise',
        textareas: [{ key: 'init', label: 'Prompt initial' }],
    },
    'enrich-opportunity-field-place': {
        zodSchema: opportunity_1.opportunitySchema.asideFields.place,
        description: 'Enrichissement du lieu',
        textareas: [{ key: 'init', label: 'Prompt initial' }],
    },
    'enrich-opportunity-field-tools': {
        zodSchema: opportunity_1.opportunitySchema.asideFields.tools,
        description: 'Enrichissement des outils',
        textareas: [{ key: 'init', label: 'Prompt initial' }],
    },
    // ─── Préparation Entretien ───────────────────────────────────
    'interview-preparation': {
        zodSchema: interview_preparation_1.interviewPreparationSchema.general,
        description: 'Preparation entretien complet',
        textareas: [{ key: 'init', label: 'Prompt initial' }],
    },
    'interview-preparation-field-activityAreas': {
        zodSchema: interview_preparation_1.interviewPreparationSchema.asideFields.activityAreas,
        description: 'Preparation entretien - Domaines activite',
        textareas: [{ key: 'init', label: 'Prompt initial' }],
    },
    'interview-preparation-field-commutingTimeRating': {
        zodSchema: interview_preparation_1.interviewPreparationRatingSchemas,
        description: 'Preparation entretien - Note temps de trajet',
        textareas: [{ key: 'init', label: 'Prompt initial' }],
    },
    // ─── Profile Matching ────────────────────────────────────────
    'profile-matching': {
        zodSchema: profile_matching_1.profileMatchingRankingResponseSchema,
        description: 'Notation des candidats pour le profile matching',
        textareas: [
            { key: 'init', label: 'Instructions de notation des candidats' },
            { key: 'scoresInstruction', label: 'Instructions de sous-scores' },
            { key: 'globalReportInstruction', label: 'Instructions de rapport global' },
        ],
    },
    'profile-matching-boolean-request': {
        zodSchema: profile_matching_1.profileMatchingBooleanRequestResponseSchema,
        description: 'Generation de mots-cles Boond pour le profile matching',
        textareas: [{ key: 'init', label: 'Prompt de generation de mots-cles Boond' }],
    },
    'profile-matching-scoring-rubric': {
        zodSchema: profile_matching_scoring_rubric_1.profileMatchingScoringRubricProposalResponseSchema,
        description: 'Proposition des criteres et ponderations de scoring pour un besoin',
        textareas: [{ key: 'init', label: 'Instructions de proposition de grille de scoring' }],
    },
    'candidate-opportunity-scoring': {
        zodSchema: candidate_opportunity_scoring_1.candidateOpportunityScoringResponseSchema,
        description: 'Scoring absolu de candidats par rapport a une opportunite',
        textareas: [{ key: 'init', label: 'Instructions de scoring absolu des candidats' }],
    },
    'interview-preparation-criteria': {
        zodSchema: candidate_opportunity_scoring_1.opportunityEvaluationCriteriaResponseSchema,
        description: "Generation des criteres d'evaluation d'une opportunite",
        textareas: [{ key: 'init', label: "Instructions de generation des criteres d'evaluation" }],
    },
    'interview-preparation-evaluation': {
        zodSchema: candidate_opportunity_scoring_1.candidateOpportunityEvaluationResponseSchema,
        description: "Evaluation detaillee d'un candidat sur un referentiel",
        textareas: [{ key: 'init', label: "Instructions d'evaluation d'une candidature" }],
    },
    // ─── Actions IA Génériques ──────────────────────────────────
    'ai-action-translate-text': {
        zodSchema: ai_actions_1.translateTextSchema,
        description: 'Traduction de texte',
        textareas: [{ key: 'init', label: 'Prompt initial' }],
    },
};
/**
 * Récupère le schéma Zod pour un type donné
 */
function getPromptSchema(type) {
    return Object.prototype.hasOwnProperty.call(exports.PROMPT_SCHEMA_REGISTRY, type) ? exports.PROMPT_SCHEMA_REGISTRY[type] : null;
}
/**
 * Liste tous les types disponibles
 */
function getAvailablePromptSchemaTypes() {
    return Object.keys(exports.PROMPT_SCHEMA_REGISTRY);
}
